/**
 * slideTemplates.js — Hardcoded constants for slide layouts, themes, and templates
 * All data is pure JS — no database tables needed.
 */

export const SLIDE_W = 960;
export const SLIDE_H = 540;

// ─────────────────────────────────────────────
// LAYOUTS — preset shape arrangements for a single slide
// ─────────────────────────────────────────────
export const LAYOUTS = [
  {
    id: 'title-slide',
    label: 'Title Slide',
    icon: '🎯',
    desc: 'Centered title + subtitle',
    shapes: [
      {
        type: 'text', x: 80, y: 160, width: 800, height: 120,
        text: 'Presentation Title', fontSize: 48, fontWeight: 'bold',
        textColor: '#1a1a1a', textAlign: 'center', fill: 'transparent',
        stroke: '', strokeWidth: 0, radius: 0, opacity: 1,
      },
      {
        type: 'text', x: 160, y: 300, width: 640, height: 60,
        text: 'Subtitle or tagline goes here', fontSize: 24, fontWeight: 'normal',
        textColor: '#555555', textAlign: 'center', fill: 'transparent',
        stroke: '', strokeWidth: 0, radius: 0, opacity: 1,
      },
    ],
  },
  {
    id: 'title-content',
    label: 'Title + Content',
    icon: '📄',
    desc: 'Title at top, body below',
    shapes: [
      {
        type: 'text', x: 60, y: 40, width: 840, height: 80,
        text: 'Slide Title', fontSize: 36, fontWeight: 'bold',
        textColor: '#1a1a1a', textAlign: 'left', fill: 'transparent',
        stroke: '', strokeWidth: 0, radius: 0, opacity: 1,
      },
      {
        type: 'text', x: 60, y: 140, width: 840, height: 340,
        text: '• Key point one\n• Key point two\n• Key point three', fontSize: 22, fontWeight: 'normal',
        textColor: '#333333', textAlign: 'left', fill: 'transparent',
        stroke: '', strokeWidth: 0, radius: 0, opacity: 1,
      },
    ],
  },
  {
    id: 'two-column',
    label: 'Two Column',
    icon: '⬛⬛',
    desc: 'Title + two side-by-side areas',
    shapes: [
      {
        type: 'text', x: 60, y: 30, width: 840, height: 70,
        text: 'Slide Title', fontSize: 36, fontWeight: 'bold',
        textColor: '#1a1a1a', textAlign: 'left', fill: 'transparent',
        stroke: '', strokeWidth: 0, radius: 0, opacity: 1,
      },
      {
        type: 'text', x: 40, y: 120, width: 420, height: 360,
        text: '• Left column\n• Point one\n• Point two', fontSize: 20, fontWeight: 'normal',
        textColor: '#333333', textAlign: 'left', fill: '#F9FAFB',
        stroke: '#E5E7EB', strokeWidth: 1, radius: 8, opacity: 1,
      },
      {
        type: 'text', x: 500, y: 120, width: 420, height: 360,
        text: '• Right column\n• Point one\n• Point two', fontSize: 20, fontWeight: 'normal',
        textColor: '#333333', textAlign: 'left', fill: '#F9FAFB',
        stroke: '#E5E7EB', strokeWidth: 1, radius: 8, opacity: 1,
      },
    ],
  },
  {
    id: 'title-image',
    label: 'Title + Image',
    icon: '🖼️',
    desc: 'Title left, image placeholder right',
    shapes: [
      {
        type: 'text', x: 40, y: 60, width: 440, height: 80,
        text: 'Slide Title', fontSize: 36, fontWeight: 'bold',
        textColor: '#1a1a1a', textAlign: 'left', fill: 'transparent',
        stroke: '', strokeWidth: 0, radius: 0, opacity: 1,
      },
      {
        type: 'text', x: 40, y: 160, width: 440, height: 280,
        text: 'Add your content here. Describe what the image shows.', fontSize: 18, fontWeight: 'normal',
        textColor: '#555555', textAlign: 'left', fill: 'transparent',
        stroke: '', strokeWidth: 0, radius: 0, opacity: 1,
      },
      {
        type: 'rect', x: 520, y: 60, width: 400, height: 400,
        text: '📷 Image', fontSize: 18, fontWeight: 'normal',
        textColor: '#9CA3AF', textAlign: 'center', fill: '#F3F4F6',
        stroke: '#D1D5DB', strokeWidth: 2, radius: 12, opacity: 1,
      },
    ],
  },
  {
    id: 'section-header',
    label: 'Section Header',
    icon: '🔷',
    desc: 'Centered bold title with accent',
    bg: '#1a1a2e',
    shapes: [
      {
        type: 'rect', x: 0, y: 0, width: 960, height: 540,
        text: '', fontSize: 16, fontWeight: 'normal',
        textColor: '#ffffff', textAlign: 'center', fill: '#1a1a2e',
        stroke: '', strokeWidth: 0, radius: 0, opacity: 1,
      },
      {
        type: 'rect', x: 360, y: 240, width: 240, height: 4,
        text: '', fontSize: 16, fontWeight: 'normal',
        textColor: '#ffffff', textAlign: 'center', fill: '#6366F1',
        stroke: '', strokeWidth: 0, radius: 2, opacity: 1,
      },
      {
        type: 'text', x: 80, y: 160, width: 800, height: 120,
        text: 'Section Title', fontSize: 56, fontWeight: 'bold',
        textColor: '#ffffff', textAlign: 'center', fill: 'transparent',
        stroke: '', strokeWidth: 0, radius: 0, opacity: 1,
      },
    ],
  },
  {
    id: 'blank',
    label: 'Blank',
    icon: '⬜',
    desc: 'Clean empty slide',
    shapes: [],
  },
];

// ─────────────────────────────────────────────
// THEMES — color palettes applied to all slides
// ─────────────────────────────────────────────
export const THEMES = [
  {
    id: 'corporate',
    label: 'Corporate',
    bg: '#ffffff',
    textColor: '#1a1a1a',
    accentColor: '#2563EB',
    preview: ['#ffffff', '#1a1a1a', '#2563EB'],
  },
  {
    id: 'dark',
    label: 'Dark',
    bg: '#1a1a1a',
    textColor: '#ffffff',
    accentColor: '#8B5CF6',
    preview: ['#1a1a1a', '#ffffff', '#8B5CF6'],
  },
  {
    id: 'nature',
    label: 'Nature',
    bg: '#fafaf5',
    textColor: '#2d4a1e',
    accentColor: '#16A34A',
    preview: ['#fafaf5', '#2d4a1e', '#16A34A'],
  },
  {
    id: 'warm',
    label: 'Warm',
    bg: '#fdf6e3',
    textColor: '#5c3317',
    accentColor: '#EA580C',
    preview: ['#fdf6e3', '#5c3317', '#EA580C'],
  },
  {
    id: 'ocean',
    label: 'Ocean',
    bg: '#e8f4f8',
    textColor: '#1a365d',
    accentColor: '#0284C7',
    preview: ['#e8f4f8', '#1a365d', '#0284C7'],
  },
  {
    id: 'minimal',
    label: 'Minimal',
    bg: '#ffffff',
    textColor: '#000000',
    accentColor: '#6B7280',
    preview: ['#ffffff', '#000000', '#6B7280'],
  },
];

// ─────────────────────────────────────────────
// TEMPLATES — multi-slide pre-built decks
// ─────────────────────────────────────────────

function makeId() {
  return `s_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
}

function shapeId() {
  return `shape_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`;
}

function titleSlide(title, subtitle, bg = '#ffffff', textColor = '#1a1a1a') {
  return {
    id: makeId(),
    bg,
    shapes: [
      { id: shapeId(), type: 'text', x: 80, y: 150, width: 800, height: 120, text: title, fontSize: 48, fontWeight: 'bold', textColor, textAlign: 'center', fill: 'transparent', stroke: '', strokeWidth: 0, radius: 0, opacity: 1, zIndex: 1 },
      { id: shapeId(), type: 'text', x: 160, y: 290, width: 640, height: 60, text: subtitle, fontSize: 22, fontWeight: 'normal', textColor: textColor === '#ffffff' ? '#cccccc' : '#666666', textAlign: 'center', fill: 'transparent', stroke: '', strokeWidth: 0, radius: 0, opacity: 1, zIndex: 2 },
    ],
  };
}

function contentSlide(title, bullets, bg = '#ffffff', textColor = '#1a1a1a', accentColor = '#2563EB') {
  return {
    id: makeId(),
    bg,
    shapes: [
      { id: shapeId(), type: 'rect', x: 0, y: 0, width: 960, height: 8, text: '', fontSize: 14, fontWeight: 'normal', textColor: '#fff', textAlign: 'center', fill: accentColor, stroke: '', strokeWidth: 0, radius: 0, opacity: 1, zIndex: 1 },
      { id: shapeId(), type: 'text', x: 60, y: 40, width: 840, height: 70, text: title, fontSize: 34, fontWeight: 'bold', textColor, textAlign: 'left', fill: 'transparent', stroke: '', strokeWidth: 0, radius: 0, opacity: 1, zIndex: 2 },
      { id: shapeId(), type: 'text', x: 60, y: 130, width: 840, height: 360, text: bullets.map(b => `• ${b}`).join('\n'), fontSize: 20, fontWeight: 'normal', textColor: textColor === '#ffffff' ? '#cccccc' : '#444444', textAlign: 'left', fill: 'transparent', stroke: '', strokeWidth: 0, radius: 0, opacity: 1, zIndex: 3 },
    ],
  };
}

function sectionSlide(title, bg = '#1a1a2e', textColor = '#ffffff', accentColor = '#6366F1') {
  return {
    id: makeId(),
    bg,
    shapes: [
      { id: shapeId(), type: 'rect', x: 0, y: 0, width: 960, height: 540, text: '', fontSize: 14, fontWeight: 'normal', textColor: '#fff', textAlign: 'center', fill: bg, stroke: '', strokeWidth: 0, radius: 0, opacity: 1, zIndex: 1 },
      { id: shapeId(), type: 'rect', x: 380, y: 250, width: 200, height: 4, text: '', fontSize: 14, fontWeight: 'normal', textColor: '#fff', textAlign: 'center', fill: accentColor, stroke: '', strokeWidth: 0, radius: 2, opacity: 1, zIndex: 2 },
      { id: shapeId(), type: 'text', x: 80, y: 170, width: 800, height: 100, text: title, fontSize: 52, fontWeight: 'bold', textColor, textAlign: 'center', fill: 'transparent', stroke: '', strokeWidth: 0, radius: 0, opacity: 1, zIndex: 3 },
    ],
  };
}

export const TEMPLATES = [
  // ── 1. Business Pitch ──────────────────────────────────────────────────────
  {
    id: 'business-pitch',
    label: 'Business Pitch',
    icon: '💼',
    desc: '8 slides — title, problem, solution, market, traction, team, financials, contact',
    slideCount: 8,
    slides: [
      titleSlide('Company Name', 'The one-line pitch that changes everything', '#0f172a', '#ffffff'),
      contentSlide('The Problem', [
        'Pain point customers face every day',
        'Current solutions are slow, expensive, or broken',
        'Market is underserved — nobody has solved this well',
      ], '#ffffff', '#1a1a1a', '#2563EB'),
      contentSlide('Our Solution', [
        'We built X that does Y in Z minutes',
        'Simple, fast, and 10× cheaper than alternatives',
        'Works seamlessly with tools your team already uses',
      ], '#ffffff', '#1a1a1a', '#2563EB'),
      contentSlide('Market Opportunity', [
        'Total Addressable Market: $X billion',
        'Serviceable Market: $X million',
        'Growing at 30% YoY — timing is perfect',
      ], '#ffffff', '#1a1a1a', '#2563EB'),
      contentSlide('Traction', [
        '500+ customers in 6 months',
        '$50K MRR — growing 20% month over month',
        'NPS score of 72 — customers love us',
      ], '#ffffff', '#1a1a1a', '#2563EB'),
      contentSlide('The Team', [
        'CEO — 10 years in the industry, ex-Google',
        'CTO — Built and scaled systems to 10M users',
        'Advisors from top VCs and Fortune 500 companies',
      ], '#ffffff', '#1a1a1a', '#2563EB'),
      contentSlide('Financials', [
        'Raising $2M seed round',
        'Runway: 18 months post-raise',
        'Path to profitability: Month 14',
      ], '#ffffff', '#1a1a1a', '#2563EB'),
      {
        id: makeId(),
        bg: '#0f172a',
        shapes: [
          { id: shapeId(), type: 'text', x: 80, y: 140, width: 800, height: 100, text: "Let's Talk", fontSize: 52, fontWeight: 'bold', textColor: '#ffffff', textAlign: 'center', fill: 'transparent', stroke: '', strokeWidth: 0, radius: 0, opacity: 1, zIndex: 1 },
          { id: shapeId(), type: 'text', x: 160, y: 260, width: 640, height: 60, text: 'hello@company.com  ·  company.com', fontSize: 22, fontWeight: 'normal', textColor: '#94a3b8', textAlign: 'center', fill: 'transparent', stroke: '', strokeWidth: 0, radius: 0, opacity: 1, zIndex: 2 },
          { id: shapeId(), type: 'rect', x: 360, y: 350, width: 240, height: 48, text: 'Schedule a Demo →', fontSize: 16, fontWeight: 'bold', textColor: '#ffffff', textAlign: 'center', fill: '#2563EB', stroke: '', strokeWidth: 0, radius: 24, opacity: 1, zIndex: 3 },
        ],
      },
    ],
  },

  // ── 2. Project Update ──────────────────────────────────────────────────────
  {
    id: 'project-update',
    label: 'Project Update',
    icon: '📊',
    desc: '5 slides — title, status, accomplishments, next steps, questions',
    slideCount: 5,
    slides: [
      titleSlide('Project Name — Q3 Update', 'Team · Date · Version 1.0', '#1e3a5f', '#ffffff'),
      {
        id: makeId(),
        bg: '#ffffff',
        shapes: [
          { id: shapeId(), type: 'rect', x: 0, y: 0, width: 960, height: 8, text: '', fontSize: 14, fontWeight: 'normal', textColor: '#fff', textAlign: 'center', fill: '#0284C7', stroke: '', strokeWidth: 0, radius: 0, opacity: 1, zIndex: 1 },
          { id: shapeId(), type: 'text', x: 60, y: 40, width: 840, height: 70, text: 'Project Status', fontSize: 34, fontWeight: 'bold', textColor: '#1a1a1a', textAlign: 'left', fill: 'transparent', stroke: '', strokeWidth: 0, radius: 0, opacity: 1, zIndex: 2 },
          { id: shapeId(), type: 'rect', x: 60, y: 130, width: 260, height: 120, text: '🟢 On Track', fontSize: 20, fontWeight: 'bold', textColor: '#166534', textAlign: 'center', fill: '#DCFCE7', stroke: '#86EFAC', strokeWidth: 2, radius: 12, opacity: 1, zIndex: 3 },
          { id: shapeId(), type: 'rect', x: 360, y: 130, width: 260, height: 120, text: '📅 Timeline\n8 weeks remaining', fontSize: 18, fontWeight: 'normal', textColor: '#1e3a5f', textAlign: 'center', fill: '#DBEAFE', stroke: '#93C5FD', strokeWidth: 2, radius: 12, opacity: 1, zIndex: 4 },
          { id: shapeId(), type: 'rect', x: 660, y: 130, width: 260, height: 120, text: '💰 Budget\n$42K of $60K used', fontSize: 18, fontWeight: 'normal', textColor: '#713f12', textAlign: 'center', fill: '#FEF9C3', stroke: '#FDE047', strokeWidth: 2, radius: 12, opacity: 1, zIndex: 5 },
          { id: shapeId(), type: 'text', x: 60, y: 280, width: 840, height: 220, text: '• Phase 1 complete — design approved\n• Phase 2 in progress — 60% done\n• Stakeholder review scheduled for next Friday', fontSize: 18, fontWeight: 'normal', textColor: '#444444', textAlign: 'left', fill: 'transparent', stroke: '', strokeWidth: 0, radius: 0, opacity: 1, zIndex: 6 },
        ],
      },
      contentSlide('Accomplishments', [
        'Delivered user research with 40 interviews',
        'Launched beta to 200 early users — 4.6/5 rating',
        'Integrated with 3 third-party APIs ahead of schedule',
        'Reduced load time by 60% through caching improvements',
      ], '#ffffff', '#1a1a1a', '#0284C7'),
      contentSlide('Next Steps', [
        'Complete Phase 2 development by end of month',
        'Conduct QA testing across all platforms',
        'Prepare launch materials and documentation',
        'Schedule stakeholder sign-off meeting',
      ], '#ffffff', '#1a1a1a', '#0284C7'),
      sectionSlide('Questions?', '#1e3a5f', '#ffffff', '#0284C7'),
    ],
  },

  // ── 3. Product Launch ──────────────────────────────────────────────────────
  {
    id: 'product-launch',
    label: 'Product Launch',
    icon: '🚀',
    desc: '6 slides — title, overview, features ×3, pricing, CTA',
    slideCount: 6,
    slides: [
      {
        id: makeId(),
        bg: '#0f172a',
        shapes: [
          { id: shapeId(), type: 'rect', x: 0, y: 0, width: 960, height: 540, text: '', fontSize: 14, fontWeight: 'normal', textColor: '#fff', textAlign: 'center', fill: '#0f172a', stroke: '', strokeWidth: 0, radius: 0, opacity: 1, zIndex: 1 },
          { id: shapeId(), type: 'text', x: 80, y: 80, width: 800, height: 60, text: '🚀 INTRODUCING', fontSize: 18, fontWeight: 'bold', textColor: '#6366F1', textAlign: 'center', fill: 'transparent', stroke: '', strokeWidth: 0, radius: 0, opacity: 1, zIndex: 2 },
          { id: shapeId(), type: 'text', x: 80, y: 150, width: 800, height: 140, text: 'Product Name', fontSize: 64, fontWeight: 'bold', textColor: '#ffffff', textAlign: 'center', fill: 'transparent', stroke: '', strokeWidth: 0, radius: 0, opacity: 1, zIndex: 3 },
          { id: shapeId(), type: 'text', x: 160, y: 310, width: 640, height: 60, text: 'The tagline that makes people stop scrolling', fontSize: 22, fontWeight: 'normal', textColor: '#94a3b8', textAlign: 'center', fill: 'transparent', stroke: '', strokeWidth: 0, radius: 0, opacity: 1, zIndex: 4 },
          { id: shapeId(), type: 'rect', x: 380, y: 400, width: 200, height: 48, text: 'Available Now', fontSize: 16, fontWeight: 'bold', textColor: '#ffffff', textAlign: 'center', fill: '#6366F1', stroke: '', strokeWidth: 0, radius: 24, opacity: 1, zIndex: 5 },
        ],
      },
      contentSlide('Product Overview', [
        'What it is — one clear sentence',
        'Who it is for — your target customer',
        'Why now — the timing and market context',
        'Key differentiator — what makes it unique',
      ], '#ffffff', '#1a1a1a', '#6366F1'),
      contentSlide('Feature: Speed', [
        '10× faster than the current leading solution',
        'Real-time processing with sub-100ms response',
        'Works offline — no internet required',
      ], '#ffffff', '#1a1a1a', '#6366F1'),
      contentSlide('Feature: Simplicity', [
        'Set up in under 5 minutes — no coding required',
        'Intuitive interface loved by non-technical users',
        'One-click integrations with 50+ popular tools',
      ], '#ffffff', '#1a1a1a', '#6366F1'),
      contentSlide('Feature: Security', [
        'End-to-end encryption on all data',
        'SOC 2 Type II certified',
        'GDPR compliant — your data stays yours',
      ], '#ffffff', '#1a1a1a', '#6366F1'),
      {
        id: makeId(),
        bg: '#ffffff',
        shapes: [
          { id: shapeId(), type: 'rect', x: 0, y: 0, width: 960, height: 8, text: '', fontSize: 14, fontWeight: 'normal', textColor: '#fff', textAlign: 'center', fill: '#6366F1', stroke: '', strokeWidth: 0, radius: 0, opacity: 1, zIndex: 1 },
          { id: shapeId(), type: 'text', x: 60, y: 30, width: 840, height: 70, text: 'Simple Pricing', fontSize: 34, fontWeight: 'bold', textColor: '#1a1a1a', textAlign: 'center', fill: 'transparent', stroke: '', strokeWidth: 0, radius: 0, opacity: 1, zIndex: 2 },
          { id: shapeId(), type: 'rect', x: 60, y: 110, width: 260, height: 300, text: 'Starter\n\n$0/mo\n\nUp to 3 users\nCore features\nEmail support', fontSize: 16, fontWeight: 'normal', textColor: '#374151', textAlign: 'center', fill: '#F9FAFB', stroke: '#E5E7EB', strokeWidth: 2, radius: 16, opacity: 1, zIndex: 3 },
          { id: shapeId(), type: 'rect', x: 360, y: 90, width: 260, height: 340, text: 'Pro\n\n$29/mo\n\nUnlimited users\nAll features\nPriority support', fontSize: 16, fontWeight: 'bold', textColor: '#ffffff', textAlign: 'center', fill: '#6366F1', stroke: '', strokeWidth: 0, radius: 16, opacity: 1, zIndex: 4 },
          { id: shapeId(), type: 'rect', x: 660, y: 110, width: 260, height: 300, text: 'Enterprise\n\nCustom\n\nCustom limits\nSSO + audit logs\nDedicated support', fontSize: 16, fontWeight: 'normal', textColor: '#374151', textAlign: 'center', fill: '#F9FAFB', stroke: '#E5E7EB', strokeWidth: 2, radius: 16, opacity: 1, zIndex: 5 },
        ],
      },
    ],
  },

  // ── 4. Minimal Deck ────────────────────────────────────────────────────────
  {
    id: 'minimal-deck',
    label: 'Minimal Deck',
    icon: '⬜',
    desc: '4 slides — clean, text-focused, no distractions',
    slideCount: 4,
    slides: [
      {
        id: makeId(),
        bg: '#ffffff',
        shapes: [
          { id: shapeId(), type: 'rect', x: 60, y: 220, width: 120, height: 6, text: '', fontSize: 14, fontWeight: 'normal', textColor: '#000', textAlign: 'center', fill: '#000000', stroke: '', strokeWidth: 0, radius: 3, opacity: 1, zIndex: 1 },
          { id: shapeId(), type: 'text', x: 60, y: 120, width: 700, height: 100, text: 'Your Big Idea', fontSize: 52, fontWeight: 'bold', textColor: '#000000', textAlign: 'left', fill: 'transparent', stroke: '', strokeWidth: 0, radius: 0, opacity: 1, zIndex: 2 },
          { id: shapeId(), type: 'text', x: 60, y: 250, width: 600, height: 60, text: 'A clear, simple subtitle', fontSize: 22, fontWeight: 'normal', textColor: '#6B7280', textAlign: 'left', fill: 'transparent', stroke: '', strokeWidth: 0, radius: 0, opacity: 1, zIndex: 3 },
        ],
      },
      {
        id: makeId(),
        bg: '#ffffff',
        shapes: [
          { id: shapeId(), type: 'text', x: 60, y: 60, width: 840, height: 80, text: 'The Context', fontSize: 38, fontWeight: 'bold', textColor: '#000000', textAlign: 'left', fill: 'transparent', stroke: '', strokeWidth: 0, radius: 0, opacity: 1, zIndex: 1 },
          { id: shapeId(), type: 'rect', x: 60, y: 150, width: 4, height: 300, text: '', fontSize: 14, fontWeight: 'normal', textColor: '#000', textAlign: 'center', fill: '#000000', stroke: '', strokeWidth: 0, radius: 2, opacity: 1, zIndex: 2 },
          { id: shapeId(), type: 'text', x: 90, y: 150, width: 810, height: 300, text: 'Explain the situation in plain language. What is happening? Why does it matter? Keep it to 3–4 sentences maximum. Less is more.', fontSize: 22, fontWeight: 'normal', textColor: '#374151', textAlign: 'left', fill: 'transparent', stroke: '', strokeWidth: 0, radius: 0, opacity: 1, zIndex: 3 },
        ],
      },
      {
        id: makeId(),
        bg: '#000000',
        shapes: [
          { id: shapeId(), type: 'text', x: 80, y: 180, width: 800, height: 120, text: 'The Key Insight', fontSize: 48, fontWeight: 'bold', textColor: '#ffffff', textAlign: 'center', fill: 'transparent', stroke: '', strokeWidth: 0, radius: 0, opacity: 1, zIndex: 1 },
          { id: shapeId(), type: 'text', x: 160, y: 320, width: 640, height: 60, text: 'One sentence. Make it count.', fontSize: 20, fontWeight: 'normal', textColor: '#9CA3AF', textAlign: 'center', fill: 'transparent', stroke: '', strokeWidth: 0, radius: 0, opacity: 1, zIndex: 2 },
        ],
      },
      {
        id: makeId(),
        bg: '#ffffff',
        shapes: [
          { id: shapeId(), type: 'text', x: 60, y: 60, width: 840, height: 80, text: 'What Happens Next', fontSize: 38, fontWeight: 'bold', textColor: '#000000', textAlign: 'left', fill: 'transparent', stroke: '', strokeWidth: 0, radius: 0, opacity: 1, zIndex: 1 },
          { id: shapeId(), type: 'text', x: 60, y: 160, width: 840, height: 280, text: '1. First action item\n2. Second action item\n3. Third action item', fontSize: 26, fontWeight: 'normal', textColor: '#374151', textAlign: 'left', fill: 'transparent', stroke: '', strokeWidth: 0, radius: 0, opacity: 1, zIndex: 2 },
        ],
      },
    ],
  },

  // ── 5. Team / About Us ─────────────────────────────────────────────────────
  {
    id: 'team-about',
    label: 'Team / About Us',
    icon: '👥',
    desc: '6 slides — cover, mission, values, team members, culture, join us',
    slideCount: 6,
    slides: [
      {
        id: makeId(),
        bg: '#0d9488',
        shapes: [
          { id: shapeId(), type: 'rect', x: 0, y: 0, width: 960, height: 540, text: '', fontSize: 14, fontWeight: 'normal', textColor: '#fff', textAlign: 'center', fill: '#0d9488', stroke: '', strokeWidth: 0, radius: 0, opacity: 1, zIndex: 1 },
          { id: shapeId(), type: 'rect', x: 0, y: 400, width: 960, height: 140, text: '', fontSize: 14, fontWeight: 'normal', textColor: '#fff', textAlign: 'center', fill: '#0f766e', stroke: '', strokeWidth: 0, radius: 0, opacity: 1, zIndex: 2 },
          { id: shapeId(), type: 'text', x: 80, y: 130, width: 800, height: 120, text: 'About Us', fontSize: 60, fontWeight: 'bold', textColor: '#ffffff', textAlign: 'center', fill: 'transparent', stroke: '', strokeWidth: 0, radius: 0, opacity: 1, zIndex: 3 },
          { id: shapeId(), type: 'text', x: 160, y: 270, width: 640, height: 60, text: 'Who we are, what we believe, and why it matters', fontSize: 22, fontWeight: 'normal', textColor: '#ccfbf1', textAlign: 'center', fill: 'transparent', stroke: '', strokeWidth: 0, radius: 0, opacity: 1, zIndex: 4 },
          { id: shapeId(), type: 'text', x: 160, y: 430, width: 640, height: 50, text: 'Company Name  ·  Founded Year', fontSize: 16, fontWeight: 'normal', textColor: '#99f6e4', textAlign: 'center', fill: 'transparent', stroke: '', strokeWidth: 0, radius: 0, opacity: 1, zIndex: 5 },
        ],
      },
      contentSlide('Our Mission', [
        'We exist to [solve this specific problem] for [this group of people]',
        'We believe that [core conviction that drives everything you do]',
        'Every decision we make is guided by this north star',
      ], '#ffffff', '#1a1a1a', '#0d9488'),
      {
        id: makeId(),
        bg: '#f0fdf4',
        shapes: [
          { id: shapeId(), type: 'rect', x: 0, y: 0, width: 960, height: 8, text: '', fontSize: 14, fontWeight: 'normal', textColor: '#fff', textAlign: 'center', fill: '#0d9488', stroke: '', strokeWidth: 0, radius: 0, opacity: 1, zIndex: 1 },
          { id: shapeId(), type: 'text', x: 60, y: 30, width: 840, height: 70, text: 'Our Values', fontSize: 34, fontWeight: 'bold', textColor: '#134e4a', textAlign: 'center', fill: 'transparent', stroke: '', strokeWidth: 0, radius: 0, opacity: 1, zIndex: 2 },
          { id: shapeId(), type: 'rect', x: 40, y: 110, width: 200, height: 160, text: '🤝 Trust\n\nWe default to transparency in everything we do', fontSize: 14, fontWeight: 'normal', textColor: '#134e4a', textAlign: 'center', fill: '#ccfbf1', stroke: '#99f6e4', strokeWidth: 1, radius: 12, opacity: 1, zIndex: 3 },
          { id: shapeId(), type: 'rect', x: 260, y: 110, width: 200, height: 160, text: '🚀 Impact\n\nWe measure success by the change we create', fontSize: 14, fontWeight: 'normal', textColor: '#134e4a', textAlign: 'center', fill: '#ccfbf1', stroke: '#99f6e4', strokeWidth: 1, radius: 12, opacity: 1, zIndex: 4 },
          { id: shapeId(), type: 'rect', x: 480, y: 110, width: 200, height: 160, text: '💡 Curiosity\n\nWe ask why before we ask how', fontSize: 14, fontWeight: 'normal', textColor: '#134e4a', textAlign: 'center', fill: '#ccfbf1', stroke: '#99f6e4', strokeWidth: 1, radius: 12, opacity: 1, zIndex: 5 },
          { id: shapeId(), type: 'rect', x: 700, y: 110, width: 200, height: 160, text: '❤️ Care\n\nWe treat people like people, not resources', fontSize: 14, fontWeight: 'normal', textColor: '#134e4a', textAlign: 'center', fill: '#ccfbf1', stroke: '#99f6e4', strokeWidth: 1, radius: 12, opacity: 1, zIndex: 6 },
          { id: shapeId(), type: 'text', x: 60, y: 300, width: 840, height: 200, text: '• We hold each other accountable with kindness\n• We celebrate wins and learn openly from failures\n• We build for the long term, not the next quarter', fontSize: 18, fontWeight: 'normal', textColor: '#374151', textAlign: 'left', fill: 'transparent', stroke: '', strokeWidth: 0, radius: 0, opacity: 1, zIndex: 7 },
        ],
      },
      {
        id: makeId(),
        bg: '#ffffff',
        shapes: [
          { id: shapeId(), type: 'rect', x: 0, y: 0, width: 960, height: 8, text: '', fontSize: 14, fontWeight: 'normal', textColor: '#fff', textAlign: 'center', fill: '#0d9488', stroke: '', strokeWidth: 0, radius: 0, opacity: 1, zIndex: 1 },
          { id: shapeId(), type: 'text', x: 60, y: 30, width: 840, height: 70, text: 'Meet the Team', fontSize: 34, fontWeight: 'bold', textColor: '#1a1a1a', textAlign: 'center', fill: 'transparent', stroke: '', strokeWidth: 0, radius: 0, opacity: 1, zIndex: 2 },
          { id: shapeId(), type: 'rect', x: 60, y: 110, width: 200, height: 200, text: '👤\n\nName\nCo-Founder & CEO', fontSize: 14, fontWeight: 'normal', textColor: '#134e4a', textAlign: 'center', fill: '#f0fdf4', stroke: '#99f6e4', strokeWidth: 1, radius: 12, opacity: 1, zIndex: 3 },
          { id: shapeId(), type: 'rect', x: 290, y: 110, width: 200, height: 200, text: '👤\n\nName\nCo-Founder & CTO', fontSize: 14, fontWeight: 'normal', textColor: '#134e4a', textAlign: 'center', fill: '#f0fdf4', stroke: '#99f6e4', strokeWidth: 1, radius: 12, opacity: 1, zIndex: 4 },
          { id: shapeId(), type: 'rect', x: 520, y: 110, width: 200, height: 200, text: '👤\n\nName\nHead of Design', fontSize: 14, fontWeight: 'normal', textColor: '#134e4a', textAlign: 'center', fill: '#f0fdf4', stroke: '#99f6e4', strokeWidth: 1, radius: 12, opacity: 1, zIndex: 5 },
          { id: shapeId(), type: 'rect', x: 750, y: 110, width: 170, height: 200, text: '👤\n\nName\nHead of Growth', fontSize: 14, fontWeight: 'normal', textColor: '#134e4a', textAlign: 'center', fill: '#f0fdf4', stroke: '#99f6e4', strokeWidth: 1, radius: 12, opacity: 1, zIndex: 6 },
          { id: shapeId(), type: 'text', x: 60, y: 330, width: 840, height: 170, text: 'Combined 40+ years of experience across product, engineering, and growth.\nPreviously at [Company], [Company], and [Company].', fontSize: 18, fontWeight: 'normal', textColor: '#6B7280', textAlign: 'center', fill: 'transparent', stroke: '', strokeWidth: 0, radius: 0, opacity: 1, zIndex: 7 },
        ],
      },
      contentSlide('Our Culture', [
        'Remote-first — we hire the best people, wherever they are',
        'Async by default — deep work is protected and respected',
        'Transparent salaries, equity, and decision-making',
        'We invest in your growth — $2K/year learning budget',
      ], '#ffffff', '#1a1a1a', '#0d9488'),
      {
        id: makeId(),
        bg: '#0d9488',
        shapes: [
          { id: shapeId(), type: 'rect', x: 0, y: 0, width: 960, height: 540, text: '', fontSize: 14, fontWeight: 'normal', textColor: '#fff', textAlign: 'center', fill: '#0d9488', stroke: '', strokeWidth: 0, radius: 0, opacity: 1, zIndex: 1 },
          { id: shapeId(), type: 'text', x: 80, y: 130, width: 800, height: 100, text: "We're Hiring", fontSize: 56, fontWeight: 'bold', textColor: '#ffffff', textAlign: 'center', fill: 'transparent', stroke: '', strokeWidth: 0, radius: 0, opacity: 1, zIndex: 2 },
          { id: shapeId(), type: 'text', x: 160, y: 250, width: 640, height: 60, text: 'Join us in building something that matters', fontSize: 22, fontWeight: 'normal', textColor: '#ccfbf1', textAlign: 'center', fill: 'transparent', stroke: '', strokeWidth: 0, radius: 0, opacity: 1, zIndex: 3 },
          { id: shapeId(), type: 'rect', x: 330, y: 340, width: 300, height: 50, text: 'careers@company.com', fontSize: 16, fontWeight: 'bold', textColor: '#0d9488', textAlign: 'center', fill: '#ffffff', stroke: '', strokeWidth: 0, radius: 25, opacity: 1, zIndex: 4 },
        ],
      },
    ],
  },

  // ── 6. Training / Workshop ─────────────────────────────────────────────────
  {
    id: 'training-workshop',
    label: 'Training / Workshop',
    icon: '🎓',
    desc: '7 slides — title, agenda, objectives, content ×2, activity, key takeaways',
    slideCount: 7,
    slides: [
      {
        id: makeId(),
        bg: '#1e1b4b',
        shapes: [
          { id: shapeId(), type: 'rect', x: 0, y: 0, width: 960, height: 540, text: '', fontSize: 14, fontWeight: 'normal', textColor: '#fff', textAlign: 'center', fill: '#1e1b4b', stroke: '', strokeWidth: 0, radius: 0, opacity: 1, zIndex: 1 },
          { id: shapeId(), type: 'rect', x: 0, y: 460, width: 960, height: 80, text: '', fontSize: 14, fontWeight: 'normal', textColor: '#fff', textAlign: 'center', fill: '#312e81', stroke: '', strokeWidth: 0, radius: 0, opacity: 1, zIndex: 2 },
          { id: shapeId(), type: 'text', x: 80, y: 80, width: 800, height: 60, text: '🎓 WORKSHOP', fontSize: 16, fontWeight: 'bold', textColor: '#a5b4fc', textAlign: 'center', fill: 'transparent', stroke: '', strokeWidth: 0, radius: 0, opacity: 1, zIndex: 3 },
          { id: shapeId(), type: 'text', x: 80, y: 150, width: 800, height: 130, text: 'Workshop Title', fontSize: 54, fontWeight: 'bold', textColor: '#ffffff', textAlign: 'center', fill: 'transparent', stroke: '', strokeWidth: 0, radius: 0, opacity: 1, zIndex: 4 },
          { id: shapeId(), type: 'text', x: 160, y: 300, width: 640, height: 60, text: 'Facilitator Name  ·  Date  ·  Duration', fontSize: 18, fontWeight: 'normal', textColor: '#a5b4fc', textAlign: 'center', fill: 'transparent', stroke: '', strokeWidth: 0, radius: 0, opacity: 1, zIndex: 5 },
          { id: shapeId(), type: 'text', x: 160, y: 475, width: 640, height: 40, text: 'Organization Name', fontSize: 14, fontWeight: 'normal', textColor: '#818cf8', textAlign: 'center', fill: 'transparent', stroke: '', strokeWidth: 0, radius: 0, opacity: 1, zIndex: 6 },
        ],
      },
      {
        id: makeId(),
        bg: '#ffffff',
        shapes: [
          { id: shapeId(), type: 'rect', x: 0, y: 0, width: 960, height: 8, text: '', fontSize: 14, fontWeight: 'normal', textColor: '#fff', textAlign: 'center', fill: '#4f46e5', stroke: '', strokeWidth: 0, radius: 0, opacity: 1, zIndex: 1 },
          { id: shapeId(), type: 'text', x: 60, y: 30, width: 840, height: 70, text: "Today's Agenda", fontSize: 34, fontWeight: 'bold', textColor: '#1e1b4b', textAlign: 'left', fill: 'transparent', stroke: '', strokeWidth: 0, radius: 0, opacity: 1, zIndex: 2 },
          { id: shapeId(), type: 'rect', x: 60, y: 120, width: 400, height: 60, text: '01  Welcome & Introductions', fontSize: 16, fontWeight: 'normal', textColor: '#1e1b4b', textAlign: 'left', fill: '#eef2ff', stroke: '#c7d2fe', strokeWidth: 1, radius: 8, opacity: 1, zIndex: 3 },
          { id: shapeId(), type: 'rect', x: 60, y: 195, width: 400, height: 60, text: '02  Learning Objectives', fontSize: 16, fontWeight: 'normal', textColor: '#1e1b4b', textAlign: 'left', fill: '#eef2ff', stroke: '#c7d2fe', strokeWidth: 1, radius: 8, opacity: 1, zIndex: 4 },
          { id: shapeId(), type: 'rect', x: 60, y: 270, width: 400, height: 60, text: '03  Core Content — Part 1', fontSize: 16, fontWeight: 'normal', textColor: '#1e1b4b', textAlign: 'left', fill: '#eef2ff', stroke: '#c7d2fe', strokeWidth: 1, radius: 8, opacity: 1, zIndex: 5 },
          { id: shapeId(), type: 'rect', x: 60, y: 345, width: 400, height: 60, text: '04  Core Content — Part 2', fontSize: 16, fontWeight: 'normal', textColor: '#1e1b4b', textAlign: 'left', fill: '#eef2ff', stroke: '#c7d2fe', strokeWidth: 1, radius: 8, opacity: 1, zIndex: 6 },
          { id: shapeId(), type: 'rect', x: 60, y: 420, width: 400, height: 60, text: '05  Group Activity & Discussion', fontSize: 16, fontWeight: 'normal', textColor: '#1e1b4b', textAlign: 'left', fill: '#eef2ff', stroke: '#c7d2fe', strokeWidth: 1, radius: 8, opacity: 1, zIndex: 7 },
          { id: shapeId(), type: 'rect', x: 500, y: 120, width: 400, height: 360, text: '⏱ Schedule\n\n0:00 – 0:10  Welcome\n0:10 – 0:25  Objectives\n0:25 – 0:50  Part 1\n0:50 – 1:15  Part 2\n1:15 – 1:40  Activity\n1:40 – 2:00  Takeaways', fontSize: 15, fontWeight: 'normal', textColor: '#374151', textAlign: 'left', fill: '#f9fafb', stroke: '#e5e7eb', strokeWidth: 1, radius: 12, opacity: 1, zIndex: 8 },
        ],
      },
      contentSlide('Learning Objectives', [
        'By the end of this session, you will be able to…',
        'Understand the core concepts of [Topic]',
        'Apply [Skill] in real-world scenarios',
        'Identify common pitfalls and how to avoid them',
      ], '#ffffff', '#1a1a1a', '#4f46e5'),
      contentSlide('Core Content — Part 1', [
        'Key concept #1 — explain it simply in one sentence',
        'Key concept #2 — use an analogy if helpful',
        'Key concept #3 — connect it to what learners already know',
        'Pause for questions before moving on',
      ], '#ffffff', '#1a1a1a', '#4f46e5'),
      contentSlide('Core Content — Part 2', [
        'Building on Part 1, we now look at…',
        'Advanced concept #1 — show a real example',
        'Advanced concept #2 — common mistakes to avoid',
        'How this connects to the bigger picture',
      ], '#ffffff', '#1a1a1a', '#4f46e5'),
      {
        id: makeId(),
        bg: '#faf5ff',
        shapes: [
          { id: shapeId(), type: 'rect', x: 0, y: 0, width: 960, height: 8, text: '', fontSize: 14, fontWeight: 'normal', textColor: '#fff', textAlign: 'center', fill: '#7c3aed', stroke: '', strokeWidth: 0, radius: 0, opacity: 1, zIndex: 1 },
          { id: shapeId(), type: 'text', x: 60, y: 30, width: 840, height: 70, text: '✏️ Group Activity', fontSize: 34, fontWeight: 'bold', textColor: '#4c1d95', textAlign: 'center', fill: 'transparent', stroke: '', strokeWidth: 0, radius: 0, opacity: 1, zIndex: 2 },
          { id: shapeId(), type: 'rect', x: 60, y: 110, width: 840, height: 100, text: 'Scenario: [Describe the real-world scenario or problem to solve]', fontSize: 18, fontWeight: 'normal', textColor: '#4c1d95', textAlign: 'left', fill: '#ede9fe', stroke: '#c4b5fd', strokeWidth: 1, radius: 10, opacity: 1, zIndex: 3 },
          { id: shapeId(), type: 'text', x: 60, y: 230, width: 840, height: 50, text: 'Instructions — work in groups of 3–4:', fontSize: 18, fontWeight: 'bold', textColor: '#4c1d95', textAlign: 'left', fill: 'transparent', stroke: '', strokeWidth: 0, radius: 0, opacity: 1, zIndex: 4 },
          { id: shapeId(), type: 'text', x: 60, y: 285, width: 840, height: 200, text: '1. Read the scenario and identify the core challenge\n2. Discuss as a group — what would you do and why?\n3. Prepare a 2-minute summary to share with the room\n4. Be ready to explain your reasoning', fontSize: 18, fontWeight: 'normal', textColor: '#374151', textAlign: 'left', fill: 'transparent', stroke: '', strokeWidth: 0, radius: 0, opacity: 1, zIndex: 5 },
          { id: shapeId(), type: 'rect', x: 760, y: 460, width: 160, height: 50, text: '⏱ 20 min', fontSize: 18, fontWeight: 'bold', textColor: '#ffffff', textAlign: 'center', fill: '#7c3aed', stroke: '', strokeWidth: 0, radius: 25, opacity: 1, zIndex: 6 },
        ],
      },
      {
        id: makeId(),
        bg: '#1e1b4b',
        shapes: [
          { id: shapeId(), type: 'rect', x: 0, y: 0, width: 960, height: 540, text: '', fontSize: 14, fontWeight: 'normal', textColor: '#fff', textAlign: 'center', fill: '#1e1b4b', stroke: '', strokeWidth: 0, radius: 0, opacity: 1, zIndex: 1 },
          { id: shapeId(), type: 'text', x: 80, y: 50, width: 800, height: 70, text: '🎯 Key Takeaways', fontSize: 34, fontWeight: 'bold', textColor: '#a5b4fc', textAlign: 'center', fill: 'transparent', stroke: '', strokeWidth: 0, radius: 0, opacity: 1, zIndex: 2 },
          { id: shapeId(), type: 'rect', x: 80, y: 140, width: 800, height: 70, text: '✅  Takeaway #1 — the most important thing to remember', fontSize: 18, fontWeight: 'normal', textColor: '#e0e7ff', textAlign: 'left', fill: '#312e81', stroke: '#4338ca', strokeWidth: 1, radius: 8, opacity: 1, zIndex: 3 },
          { id: shapeId(), type: 'rect', x: 80, y: 225, width: 800, height: 70, text: '✅  Takeaway #2 — the skill or framework to apply', fontSize: 18, fontWeight: 'normal', textColor: '#e0e7ff', textAlign: 'left', fill: '#312e81', stroke: '#4338ca', strokeWidth: 1, radius: 8, opacity: 1, zIndex: 4 },
          { id: shapeId(), type: 'rect', x: 80, y: 310, width: 800, height: 70, text: '✅  Takeaway #3 — the mindset shift or new perspective', fontSize: 18, fontWeight: 'normal', textColor: '#e0e7ff', textAlign: 'left', fill: '#312e81', stroke: '#4338ca', strokeWidth: 1, radius: 8, opacity: 1, zIndex: 5 },
          { id: shapeId(), type: 'text', x: 160, y: 410, width: 640, height: 60, text: 'Questions? Reach out at facilitator@company.com', fontSize: 16, fontWeight: 'normal', textColor: '#818cf8', textAlign: 'center', fill: 'transparent', stroke: '', strokeWidth: 0, radius: 0, opacity: 1, zIndex: 6 },
        ],
      },
    ],
  },
];
